import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const bugReportInvestigationGuide: AdminGuide = {
  id: "bug-report-investigation",
  title:
    "Investigate a submitted bug report (status, monday link, correlation IDs)",
  category: "Bug Reporting",
  audience: ["Support", "Ops", "CS"],
  summary:
    "Use the Admin Bug Tracker entry as the source of truth and collect the right evidence for Engineering without leaking sensitive data.",
  keywords: [
    "bug tracker",
    "monday",
    "status",
    "stage",
    "correlation id",
    "triage",
    "validate",
  ],
  location: "Admin",
  risk: "low",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.BUG_TRACKER_FEATURE,
    GUIDE_SOURCE_DOCS.ADMIN_BUG_TRACKER,
    GUIDE_SOURCE_DOCS.LOGGING,
  ],
  whatThisHelpsYouDo: [
    "Find the canonical bug report entry from a monday item.",
    "Interpret the report status/stage (queued vs running vs failed).",
    "Collect the minimum safe evidence Engineering needs to investigate.",
  ],
  whenToUse: [
    "You need to follow up on a submitted bug report.",
    "Engineering asks for the bugReportId, correlationId, or current stage.",
    "You suspect the report is stuck and needs escalation.",
  ],
  whenNotToUse: [
    "You need to share raw logs or full stored JSON externally (do not do this).",
    "You need to share tokenised URLs or unsafe screenshots (stop and escalate).",
  ],
  beforeYouStart: [
    "The monday item link (if you have it), or at least the bug report UUID.",
    "The approximate time the report was submitted (include timezone).",
  ],
  steps: [
    "Start from monday when possible. Prefer the **Submission link** column (full admin deep link).",
    "Open the report in Admin (`/bug-tracker/<FULL_UUID>`).",
    "Check the top-of-page status/stage indicators (queued vs running vs failed).",
    "Read the validation restatement and follow-up answers first (often enough to reproduce).",
    "If needed, open **Developer context** to inspect stored JSON (do not copy/paste it into tickets).",
    "Collect safe identifiers for Engineering: bugReportId, organisationId, timestamps, and correlationId (if present).",
    "If the report looks stuck, escalate with IDs and the time you checked.",
  ],
  whatSuccessLooksLike: [
    "You can open the canonical admin bug report entry from monday.",
    "You can explain the current stage (validation done / triage running / monday write done).",
    "Engineering has IDs and timestamps (without any sensitive payloads).",
  ],
  troubleshooting: [
    {
      title: "The monday item only has [BR-xxxxxxxx] in the title",
      body: [
        "Use the monday bug report id column (full UUID) or the submission link column (preferred).",
        "Then open `/bug-tracker/<FULL_UUID>` in Admin.",
      ],
    },
    {
      title: "The report looks stuck (no progress)",
      body: [
        "Capture: bugReportId, current status/stage, and the time you checked.",
        "Escalate to Engineering with those IDs and timestamps.",
      ],
    },
  ],
  escalateWhen: [
    "The report appears stuck in a stage for an unusually long time.",
    "The report shows an error code/message you don’t understand.",
    "Engineering asks for log correlation (provide correlationId and timestamps only).",
  ],
  relatedGuides: [
    { label: "Submitting a bug report", guideId: "bug-reporting" },
    { label: "Spoofing safely", guideId: "spoofing-safely" },
  ],
  relatedAdminPaths: [
    { label: "Bug Tracker", path: "/bug-tracker" },
    { label: "Bug Reports (recent)", path: "/bug-tracker/recent" },
  ],
};
